import React, { useCallback, useEffect, useMemo, useState } from "react";
import { IonIcon, IonSpinner } from "@ionic/react";
import {
  arrowBackOutline,
  checkmarkOutline,
  closeOutline,
  refreshOutline,
} from "ionicons/icons";
import {
  collection,
  getCountFromServer,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import firebaseApp, { db } from "../../lib/firebase/firebase";
import "./SupportOpsPage.css";
import TicketCorrespondence from "./TicketCorrespondence";

const MAX_ITEMS = 50;
const reviewSupportAction = httpsCallable(
  getFunctions(firebaseApp),
  "reviewSupportAction"
);

function fmtDate(value) {
  if (!value) return "—";
  try {
    const date = value?.toDate
      ? value.toDate()
      : value?.seconds
        ? new Date(value.seconds * 1000)
        : new Date(value);
    return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
  } catch {
    return "—";
  }
}

function metricLabel(intent) {
  if (intent === "refund_request") return "refund";
  if (intent === "cancellation_request") return "cancel";
  return intent || "unknown";
}

function displayValue(value) {
  if (value == null || value === "") return "—";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function pendingTicketId(row) {
  return row.ticketId || row.requestTicketId || row.supportTicketId || null;
}

function proposedTarget(row) {
  return (
    row.proposedTarget ??
    row.exactTarget ??
    row.target ??
    row.actionTarget ??
    row.metadata ??
    null
  );
}

function actionStatusClass(outcome) {
  if (outcome === "executed" || outcome === "approved") return "sop-pill-ok";
  if (outcome === "failed" || outcome === "rejected") return "sop-pill-bad";
  return "sop-pill-shadow";
}

const SupportOpsPage = ({ onBack }) => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [tickets, setTickets] = useState([]);
  const [pending, setPending] = useState([]);
  const [actions, setActions] = useState([]);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [reviewingId, setReviewingId] = useState(null);
  const [reviewError, setReviewError] = useState("");
  const [summary, setSummary] = useState({
    last24hTickets: 0,
    last24hEscalated: 0,
    last24hReplySent: 0,
    awaitingCustomer: 0,
    readyForReview: 0,
    last24hRefundActions: 0,
    last24hCancelActions: 0,
  });

  const loadDashboard = useCallback(async () => {
    setError("");
    const dayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);

    try {
      const ticketsCollection = collection(db, "supportTickets");
      const pendingCollection = collection(db, "supportPendingActions");
      const actionsCollection = collection(db, "supportAgentActions");

      const [
        ticketsSnap,
        pendingSnap,
        actionsSnap,
        tickets24h,
        escalated24h,
        replySent24h,
        awaitingCustomerCount,
        readyForReviewCount,
        refundActions24h,
        cancelActions24h,
      ] = await Promise.all([
        getDocs(
          query(
            ticketsCollection,
            orderBy("createdAt", "desc"),
            limit(MAX_ITEMS)
          )
        ),
        getDocs(
          query(
            pendingCollection,
            orderBy("updatedAt", "desc"),
            limit(MAX_ITEMS)
          )
        ),
        getDocs(
          query(
            actionsCollection,
            orderBy("createdAt", "desc"),
            limit(MAX_ITEMS)
          )
        ),
        getCountFromServer(
          query(ticketsCollection, where("createdAt", ">=", dayAgo))
        ),
        getCountFromServer(
          query(
            ticketsCollection,
            where("escalated", "==", true),
            where("createdAt", ">=", dayAgo)
          )
        ),
        getCountFromServer(
          query(
            ticketsCollection,
            where("replySent", "==", true),
            where("createdAt", ">=", dayAgo)
          )
        ),
        getCountFromServer(
          query(pendingCollection, where("status", "==", "awaiting_customer"))
        ),
        getCountFromServer(
          query(pendingCollection, where("status", "==", "ready_for_review"))
        ),
        getCountFromServer(
          query(
            actionsCollection,
            where("action", "==", "refund_charge"),
            where("outcome", "==", "executed"),
            where("createdAt", ">=", dayAgo)
          )
        ),
        getCountFromServer(
          query(
            actionsCollection,
            where("action", "==", "cancel_at_period_end"),
            where("outcome", "==", "executed"),
            where("createdAt", ">=", dayAgo)
          )
        ),
      ]);

      const ticketRows = ticketsSnap.docs.map((item) => ({
        id: item.id,
        ...item.data(),
      }));
      setTickets(ticketRows);
      setPending(
        pendingSnap.docs.map((item) => ({ id: item.id, ...item.data() }))
      );
      setActions(
        actionsSnap.docs.map((item) => ({ id: item.id, ...item.data() }))
      );
      setSelectedTicketId((current) => {
        if (current && ticketRows.some((ticket) => ticket.id === current)) {
          return current;
        }
        return ticketRows[0]?.id || null;
      });
      setSummary({
        last24hTickets: tickets24h.data().count || 0,
        last24hEscalated: escalated24h.data().count || 0,
        last24hReplySent: replySent24h.data().count || 0,
        awaitingCustomer: awaitingCustomerCount.data().count || 0,
        readyForReview: readyForReviewCount.data().count || 0,
        last24hRefundActions: refundActions24h.data().count || 0,
        last24hCancelActions: cancelActions24h.data().count || 0,
      });
    } catch (loadError) {
      console.error("SupportOpsPage load failed:", loadError);
      setError(
        "Could not load support activity. Confirm this account has active admin access."
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const selectedTicket = useMemo(
    () => tickets.find((ticket) => ticket.id === selectedTicketId) || null,
    [selectedTicketId, tickets]
  );

  const selectedPending = useMemo(() => {
    if (!selectedTicket) return [];
    return pending.filter(
      (item) =>
        pendingTicketId(item) === selectedTicket.id ||
        (!pendingTicketId(item) && item.email === selectedTicket.email)
    );
  }, [pending, selectedTicket]);

  const selectedActions = useMemo(() => {
    if (!selectedTicket) return [];
    const audits = actions.filter(
      (item) =>
        item.ticketId === selectedTicket.id ||
        item.supportTicketId === selectedTicket.id
    );
    const embedded = Array.isArray(selectedTicket.actions)
      ? selectedTicket.actions.map((item, index) => ({
          id: `${selectedTicket.id}-embedded-${index}`,
          outcome:
            item.outcome ||
            (item.executed
              ? item.success
                ? "executed"
                : "failed"
              : "proposed"),
          ...item,
        }))
      : [];
    return audits.length > 0 ? audits : embedded;
  }, [actions, selectedTicket]);

  const readyForReview = pending.filter(
    (item) => item.status === "ready_for_review"
  );
  const awaitingCustomer = pending.filter(
    (item) => item.status === "awaiting_customer"
  );

  const reviewPending = async (pendingId, decision) => {
    setReviewingId(pendingId);
    setReviewError("");
    try {
      await reviewSupportAction({ pendingId, decision });
      await loadDashboard();
    } catch (reviewFailure) {
      console.error("Support action review failed:", reviewFailure);
      setReviewError(
        reviewFailure?.message || "The review decision could not be submitted."
      );
    } finally {
      setReviewingId(null);
    }
  };

  const selectPendingTicket = (item) => {
    const ticketId = pendingTicketId(item);
    const matchingTicket = ticketId
      ? tickets.find((ticket) => ticket.id === ticketId)
      : tickets.find((ticket) => ticket.email === item.email);
    if (matchingTicket) setSelectedTicketId(matchingTicket.id);
  };

  return (
    <div className="support-ops-page">
      <div className="sop-header">
        <button className="sop-icon-btn" onClick={onBack} aria-label="Back">
          <IonIcon icon={arrowBackOutline} />
        </button>
        <h2 className="sop-title">Support Ops</h2>
        <button
          className="sop-icon-btn"
          onClick={() => {
            setRefreshing(true);
            loadDashboard();
          }}
          disabled={refreshing}
          aria-label="Refresh"
        >
          {refreshing ? (
            <IonSpinner name="lines" />
          ) : (
            <IonIcon icon={refreshOutline} />
          )}
        </button>
      </div>

      {loading ? (
        <div className="sop-loading">
          <IonSpinner name="crescent" />
          <p>Loading support activity…</p>
        </div>
      ) : (
        <div className="sop-scroll">
          {error ? <div className="sop-error">{error}</div> : null}
          {reviewError ? <div className="sop-error">{reviewError}</div> : null}

          <div className="sop-cards">
            {[
              ["Tickets (24h)", summary.last24hTickets],
              ["Replies sent (24h)", summary.last24hReplySent],
              ["Escalated (24h)", summary.last24hEscalated],
              ["Awaiting customer", summary.awaitingCustomer],
              ["Ready for review", summary.readyForReview],
              ["Refunds executed (24h)", summary.last24hRefundActions],
              ["Cancels executed (24h)", summary.last24hCancelActions],
            ].map(([label, value]) => (
              <div className="sop-card" key={label}>
                <p className="sop-card-label">{label}</p>
                <p className="sop-card-value">{value}</p>
              </div>
            ))}
          </div>

          <section className="sop-section">
            <h3>Ready for Review</h3>
            <div className="sop-list">
              {readyForReview.map((row) => (
                <div
                  key={row.id}
                  className="sop-review-row"
                  onClick={() => selectPendingTicket(row)}
                >
                  <div className="sop-row-copy">
                    <p className="sop-row-title">
                      {row.email || "unknown"} ·{" "}
                      {row.action || metricLabel(row.intent)}
                    </p>
                    <p className="sop-row-meta">
                      {row.rationale || row.requestSummary || "Review required"}{" "}
                      · {fmtDate(row.updatedAt || row.createdAt)}
                    </p>
                    <pre className="sop-target">
                      {displayValue(proposedTarget(row))}
                    </pre>
                  </div>
                  <div className="sop-review-controls">
                    <button
                      className="sop-action-btn sop-action-btn--approve"
                      disabled={reviewingId === row.id}
                      onClick={(event) => {
                        event.stopPropagation();
                        reviewPending(row.id, "approve");
                      }}
                    >
                      {reviewingId === row.id ? (
                        <IonSpinner name="lines" />
                      ) : (
                        <IonIcon icon={checkmarkOutline} />
                      )}
                      Approve
                    </button>
                    <button
                      className="sop-action-btn sop-action-btn--reject"
                      disabled={reviewingId === row.id}
                      onClick={(event) => {
                        event.stopPropagation();
                        reviewPending(row.id, "reject");
                      }}
                    >
                      <IonIcon icon={closeOutline} />
                      Reject
                    </button>
                  </div>
                </div>
              ))}
              {readyForReview.length === 0 ? (
                <p className="sop-empty">No actions are waiting for review.</p>
              ) : null}
            </div>
          </section>

          <section className="sop-section">
            <h3>Awaiting Customer</h3>
            <p className="sop-subtle">
              refund:{" "}
              {
                awaitingCustomer.filter(
                  (item) => item.intent === "refund_request"
                ).length
              }{" "}
              · cancel:{" "}
              {
                awaitingCustomer.filter(
                  (item) => item.intent === "cancellation_request"
                ).length
              }
            </p>
            <div className="sop-list">
              {awaitingCustomer.slice(0, 15).map((row) => (
                <button
                  key={row.id}
                  className="sop-row sop-row--selectable"
                  onClick={() => selectPendingTicket(row)}
                >
                  <span className="sop-row-copy">
                    <span className="sop-row-title">
                      {row.email || "unknown"} · {metricLabel(row.intent)}
                    </span>
                    <span className="sop-row-meta">
                      attempts: {row.attempts || 1} · {fmtDate(row.updatedAt)}
                    </span>
                  </span>
                  <span className="sop-pill">awaiting</span>
                </button>
              ))}
              {awaitingCustomer.length === 0 ? (
                <p className="sop-empty">No customer confirmations pending.</p>
              ) : null}
            </div>
          </section>

          <div className="sop-workspace">
            <section className="sop-section sop-ticket-list">
              <h3>Recent Tickets</h3>
              <div className="sop-list">
                {tickets.map((row) => (
                  <button
                    key={row.id}
                    className={`sop-row sop-row--selectable ${
                      selectedTicketId === row.id ? "sop-row--selected" : ""
                    }`}
                    onClick={() => setSelectedTicketId(row.id)}
                  >
                    <span className="sop-row-copy">
                      <span className="sop-row-title">
                        {row.email || "unknown"} · {row.intent || "unknown"}
                      </span>
                      <span className="sop-row-meta">
                        {row.subject || "(no subject)"} ·{" "}
                        {fmtDate(row.createdAt)}
                      </span>
                    </span>
                    {row.shadowMode ? (
                      <span className="sop-pill sop-pill-shadow">shadow</span>
                    ) : null}
                  </button>
                ))}
                {tickets.length === 0 ? (
                  <p className="sop-empty">No tickets yet.</p>
                ) : null}
              </div>
            </section>

            <section className="sop-section sop-detail">
              <h3>Ticket Detail</h3>
              {selectedTicket ? (
                <>
                  <div className="sop-detail-heading">
                    <div>
                      <p className="sop-detail-title">
                        {selectedTicket.subject || "(no subject)"}
                      </p>
                      <p className="sop-row-meta">
                        {selectedTicket.email || "unknown"} ·{" "}
                        {selectedTicket.intent || "unknown"} ·{" "}
                        {fmtDate(selectedTicket.createdAt)}
                      </p>
                    </div>
                    <span className="sop-pill">
                      {selectedTicket.replySent
                        ? "replied"
                        : selectedTicket.escalated
                          ? "escalated"
                          : "open"}
                    </span>
                  </div>

                  <DetailBlock
                    label="Original body"
                    value={selectedTicket.body || selectedTicket.originalBody}
                  />
                  <TicketCorrespondence ticket={selectedTicket} />
                  <DetailBlock
                    label={selectedTicket.lastManualReplyMessageId ? "Original suggested draft — superseded by manual reply" : "Suggested draft — not proof of sending"}
                    value={
                      selectedTicket.draftBody ||
                      selectedTicket.draft?.body ||
                      selectedPending.find((item) => item.draft)?.draft
                    }
                  />
                  <DetailBlock
                    label="Rationale"
                    value={
                      selectedTicket.escalationReason ||
                      selectedTicket.resolutionSummary ||
                      selectedTicket.classifierSummary ||
                      selectedPending.find(
                        (item) => item.rationale || item.requestSummary
                      )?.rationale ||
                      selectedPending.find((item) => item.requestSummary)
                        ?.requestSummary
                    }
                  />
                  <DetailBlock
                    label="Proposed exact target"
                    value={
                      proposedTarget(
                        selectedPending.find(
                          (item) => proposedTarget(item) != null
                        ) || {}
                      ) ||
                      selectedTicket.proposedTarget ||
                      selectedTicket.actionTarget
                    }
                    pre
                  />

                  <div className="sop-detail-block">
                    <p className="sop-detail-label">Action history</p>
                    <div className="sop-list">
                      {selectedActions.map((row) => {
                        const outcome = row.outcome || "unknown";
                        return (
                          <div className="sop-row" key={row.id}>
                            <div className="sop-row-copy">
                              <p className="sop-row-title">
                                {row.action || "support action"}
                              </p>
                              <p className="sop-row-meta">
                                {row.description || row.detail || "—"} ·{" "}
                                {fmtDate(row.createdAt)}
                              </p>
                            </div>
                            <span
                              className={`sop-pill ${actionStatusClass(outcome)}`}
                            >
                              {outcome}
                            </span>
                          </div>
                        );
                      })}
                      {selectedActions.length === 0 ? (
                        <p className="sop-empty">No action history.</p>
                      ) : null}
                    </div>
                  </div>
                </>
              ) : (
                <p className="sop-empty">Select a ticket to inspect it.</p>
              )}
            </section>
          </div>

          <section className="sop-section">
            <h3>Recent Action Audit</h3>
            <div className="sop-list">
              {actions.slice(0, 20).map((row) => {
                const outcome = row.outcome || "unknown";
                return (
                  <div className="sop-row" key={row.id}>
                    <div className="sop-row-copy">
                      <p className="sop-row-title">
                        {row.action || "unknown"} · {row.email || "unknown"}
                      </p>
                      <p className="sop-row-meta">
                        {row.description || "—"} · {fmtDate(row.createdAt)}
                      </p>
                    </div>
                    <span className={`sop-pill ${actionStatusClass(outcome)}`}>
                      {outcome}
                    </span>
                  </div>
                );
              })}
              {actions.length === 0 ? (
                <p className="sop-empty">No action audit records yet.</p>
              ) : null}
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

const DetailBlock = ({ label, value, pre = false }) => (
  <div className="sop-detail-block">
    <p className="sop-detail-label">{label}</p>
    {pre ? (
      <pre className="sop-detail-text">{displayValue(value)}</pre>
    ) : (
      <p className="sop-detail-text">{displayValue(value)}</p>
    )}
  </div>
);

export default SupportOpsPage;
